import { currentImpersonation, impersonationRenamed } from '../lib/api/impersonation';
import { clubSession, memberSession } from '../lib/auth/instance';
import { customerContext } from '../lib/api/customerContext';
import { isApiMode } from '../lib/api/baseUrl';

// ─────────────────────────────────────────────────────────────────────
// WHAT THE ACTING ORGANIZATION IS CALLED (#766).
//
// `tenants.name` is the canonical club name — the public directory searches
// it, `/me/clubs` lists it, a member's session reports it. `club_info_v1`
// used to carry a second, independently writable one, which is how a club
// became unfindable under the name it calls itself.
//
// This resolves the canonical name for whichever organization the portal is
// ACTING AS, so the blob's copy never wins.
//
// ── THE SURFACE DECIDES WHICH ORGANIZATION THAT IS ─────────────────
//
// `customerContext()` is the thing that knows, and asking it is not optional:
// a browser can hold a MEMBER session and an ADMINISTRATIVE one at the same
// time, which `customerContext.ts` documents at length because a fixed order
// over the session inventory was a wrong-tenant bug once already.
//
// `useClubInfo` runs on both surfaces. Without the surface check, a member
// looking at club M while an administrator session for club A happened to be
// live had the bootstrap's correct name M overridden by A — one club's name on
// another's screen, which is the whole defect class #766 is about, arriving
// from the other direction.
//
// So the three surfaces answer from three different places:
//
//   member  the member's own session. `/member/me` and the bootstrap both
//           report `tenants.name` for the club the SESSION carries, and a
//           member has exactly one.
//   staff   an impersonation context, which is how platform authority reaches
//           a customer organization at all (RBAC-3). An operator has no scope
//           of their own there: reading `currentScope` would answer with
//           whatever club they administer themselves, or with nothing.
//   club    the SELECTED SCOPE. `/auth/scopes` sends each membership's name
//           off the authority snapshot, so `currentScope.name` is already
//           `tenants.name` with no extra request.
// ─────────────────────────────────────────────────────────────────────

/**
 * The acting organization's canonical name, or null when there is none to use.
 *
 * NULL IS A REAL ANSWER, not a failure: before a scope is selected, outside
 * API mode, and on the member surface there is no organization row to read
 * from, and inventing one would be worse than leaving the blob's value alone.
 */
export function canonicalClubName(): string | null {
  // ── NOT IN API MODE, NOT CANONICAL ───────────────────────────────
  //
  // The memory backend has no organizations at all. There is nothing to be
  // canonical ABOUT, so the blob is all there is and must be left alone —
  // treating an empty answer as authoritative there would blank the name on
  // every screen.
  if (!isApiMode()) return null;

  // ── THE ACTING SURFACE, ASKED RATHER THAN GUESSED ─────────────────
  //
  // Not «whichever session exists»: both can, and the surface is what says
  // which one the app is rendering.
  const surface = customerContext().surface;

  if (surface === 'member') {
    // The member's own club, from their own session. An administrative
    // session elsewhere in the tab is not this screen's organization.
    return memberSession.state().club?.name || null;
  }

  // A context being active IS the acting organization, so this is checked
  // before the scope rather than as a fallback behind it.
  const impersonated = currentImpersonation();
  if (impersonated) return impersonated.organization.name || null;

  if (surface === 'club') return clubSession.state().currentScope?.name || null;

  // STAFF WITH NO CONTEXT — `/sa` itself, which is not a club screen. There is
  // no acting organization, so there is no canonical name to project.
  return null;
}

/**
 * Tell the name's SOURCE that the acting organization has been renamed (#766).
 *
 * ── WHY A WRITE-BACK IS NEEDED AT ALL (#776 review) ────────────────
 *
 * Both sources are SNAPSHOTS. `currentScope.name` comes from `/auth/scopes` at
 * sign-in; `currentImpersonation().organization.name` from the moment the
 * context started. The projection reads them on every render, so after a club
 * renamed itself the snapshot became the stale copy and the projection put the
 * OLD name straight back over the one the server had just accepted — the
 * header disagreeing with the directory, and settings re-opening on a name the
 * server would refuse as an expectation.
 *
 * Each of my two slices was right alone: #775 saves and #776 projects. Together
 * the projection won.
 *
 * ── WHAT IT DOES NOT DO ────────────────────────────────────────────
 *
 * Not `refreshScopes()`: that would re-decide the SELECTION too, and a save is
 * not a reason to move which club somebody is working in. Not a guess either —
 * it takes the name the SERVER ACCEPTED, and each source ignores a tenant it is
 * not already for, so a rename can never put one organization's name on
 * another.
 *
 * `tenantId` is the ACTING organization's, resolved by the caller from the same
 * context the projection reads.
 */
export function acknowledgeRename(tenantId: string | null, name: string): void {
  if (!tenantId || !name) return;
  // BOTH sources are told, and each refuses what is not its own: an operator
  // may hold a scope list AND a context, and only the one the acting surface
  // reads from matters. Telling both is simpler than deciding here, and
  // neither can be made to accept another organization's name.
  impersonationRenamed(tenantId, name);
  clubSession.scopeRenamed(tenantId, name);
}

/** The ACTING organization's id, from the same context the name comes from. */
export function actingOrganizationId(): string | null {
  if (!isApiMode()) return null;
  const surface = customerContext().surface;
  if (surface === 'member') return memberSession.state().club?.id ?? null;
  const impersonated = currentImpersonation();
  if (impersonated) return impersonated.organization.id || null;
  if (surface === 'club') return clubSession.state().currentScope?.tenantId ?? null;
  return null;
}

/**
 * The club-info document with its name replaced by the organization's.
 *
 * PROJECTED AFTER THE READ, over whatever the blob decoded to — so a stale
 * `name` in the document can never reach a screen, and a document with no
 * name at all still shows the real one.
 */
export function withCanonicalName<T extends { name?: string }>(info: T): T {
  const canonical = canonicalClubName();
  return canonical === null ? info : { ...info, name: canonical };
}
